import React, { useState, useEffect, useCallback } from 'react';
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Calendar, Tag, ThumbsUp, Share2, ExternalLink, TrendingUp, Zap, Clock, Users, Bell } from 'lucide-react';
import { fetchTrends } from '../utils/trendsFetcher';
import { saveToGitHub } from '../utils/githubUtils';
import { useInView } from 'react-intersection-observer';

const NewsletterSection = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { ref, inView } = useInView();

  const topics = [
    { id: 'all', label: 'All Topics' },
    { id: 'aiTrends', label: 'AI Development' },
    { id: 'testingTrends', label: 'Testing' },
    { id: 'industryTrends', label: 'Industry News' }
  ];

  const loadTrends = useCallback(async () => {
    try {
      setLoading(true);
      const { trends, hasMore: more, page: currentPage } = await fetchTrends(page);
      
      setNewsletters(prev => {
        if (currentPage === 1) return Object.values(trends).flat();
        return [...prev, ...Object.values(trends).flat()];
      });
      
      setHasMore(more);
    } catch (err) {
      setError(err.message || 'Failed to fetch trends');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadTrends();
  }, [loadTrends]);

  useEffect(() => {
    if (inView && !loading && hasMore) {
      setPage(prev => prev + 1);
    }
  }, [inView, loading, hasMore]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      const subscribers = JSON.parse(localStorage.getItem('newsletter_subscribers') || '[]');
      if (!subscribers.includes(email)) {
        subscribers.push(email);
        localStorage.setItem('newsletter_subscribers', JSON.stringify(subscribers));
        setSubscribeStatus('Successfully subscribed! You will receive daily updates.');
      } else {
        setSubscribeStatus('You are already subscribed!');
      }
      setEmail('');
    } catch (error) {
      setSubscribeStatus('Failed to subscribe. Please try again.');
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-4">
            <TrendingUp className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-blue-600 font-semibold">Daily Updates</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Latest Tech & AI <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Insights</span>
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Stay ahead with 10+ curated stories daily covering AI developments, accessibility trends, and industry breakthroughs
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl shadow-sm text-center border border-gray-100">
            <div className="text-2xl font-bold text-blue-600">10+</div>
            <div className="text-sm text-gray-600">Stories Daily</div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm text-center border border-gray-100">
            <div className="text-2xl font-bold text-green-600">1K+</div>
            <div className="text-sm text-gray-600">Subscribers</div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm text-center border border-gray-100">
            <div className="text-2xl font-bold text-purple-600">95%</div>
            <div className="text-sm text-gray-600">Open Rate</div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm text-center border border-gray-100">
            <div className="text-2xl font-bold text-orange-600">24/7</div>
            <div className="text-sm text-gray-600">Updated</div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content - News Section */}
          <div className="lg:w-2/3">
            
            {/* Topic Filter */}
            <div className="mb-8 flex flex-wrap gap-3">
              {topics.map(topic => (
                <Button
                  key={topic.id}
                  onClick={() => setSelectedTopic(topic.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    selectedTopic === topic.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {topic.label}
                </Button>
              ))}
            </div>

            {/* News Grid */}
            <div className="grid gap-6">
              {newsletters.map((item, index) => (
                <Card key={`${item.link}-${index}`} className="bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="mr-2 w-4 h-4" />
                        <span>{item.pubDate}</span>
                      </div>
                      <div className="inline-flex items-center px-3 py-1 bg-blue-50 rounded-full">
                        <Tag className="w-3 h-3 mr-1 text-blue-500" />
                        <span className="text-xs font-medium text-blue-700">{item.topic}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 hover:text-blue-600 transition-colors">
                      {item.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                      {item.description}
                    </p>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                      <Button
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
                        onClick={() => window.open(item.link, '_blank')}
                      >
                        Read More <ExternalLink className="w-4 h-4" />
                      </Button>
                      
                      <div className="flex gap-2">
                        <button className="p-2 hover:bg-blue-50 rounded-full transition-colors group">
                          <ThumbsUp className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                        </button>
                        <button className="p-2 hover:bg-blue-50 rounded-full transition-colors group">
                          <Share2 className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading fresh insights...</span>
              </div>
            )}

            {hasMore && !loading && (
              <div ref={ref} className="h-10" />
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-center">
                <Zap className="w-5 h-5 inline mr-2" />
                {error}
              </div>
            )}
          </div>

          {/* Enhanced Sidebar */}
          <div className="lg:w-1/3">
            <div className="space-y-6">
              
              {/* Subscription Card */}
              <div className="bg-gradient-to-br from-blue-600 to-purple-700 p-6 rounded-2xl text-white shadow-lg">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                    <Bell className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Stay Ahead</h3>
                  <p className="text-blue-100 text-sm">
                    Join 1,000+ professionals getting daily AI & tech insights
                  </p>
                </div>
                
                <form onSubmit={handleSubscribe} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      className="w-full p-3 rounded-lg border-0 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-white/50"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-white text-blue-600 hover:bg-gray-50 font-semibold py-3 rounded-lg transition-all"
                  >
                    Subscribe Free
                  </Button>
                </form>
                
                {subscribeStatus && (
                  <div className="mt-4 p-3 bg-green-500/20 rounded-lg text-green-100 text-sm text-center">
                    {subscribeStatus}
                  </div>
                )}
                
                <div className="mt-6 text-center">
                  <p className="text-xs text-blue-200">
                    ✅ No spam • ✅ Unsubscribe anytime • ✅ 10+ stories daily
                  </p>
                </div>
              </div>

              {/* Featured Topics */}
              <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Trending Topics</h3>
                <div className="space-y-3">
                  {['AI Accessibility', 'Web Compliance', 'Machine Learning', 'API Development'].map((topic, index) => (
                    <div key={topic} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">{topic}</span>
                      <div className="flex items-center text-xs text-gray-500">
                        <Users className="w-3 h-3 mr-1" />
                        {Math.floor(Math.random() * 50 + 20)}K
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSection;